import { Link } from "react-router-dom";
import { useCart } from "@/hooks/useCart";
import { useProducts } from "@/hooks/useProducts";
import { useProductsStock } from "@/hooks/useProductsStock";
import { useState, useMemo, useEffect } from "react";
import { useThemeColor, hexAlpha } from "@/hooks/useThemeColor";
import { useSectionBg } from "@/hooks/useSectionBg";
import { useDesignTokens } from "@/hooks/useDesignTokens";
import { ProductDetail } from "@/mocks/productDetails";

const categories = ["الكل", "PS5", "PS4", "PC", "Controllers", "Headsets"];
const categoryLabels: Record<string, string> = {
  "الكل": "الكل",
  "PS5": "PS5",
  "PS4": "PS4",
  "PC": "PC",
  "Controllers": "أيادي التحكم",
  "Headsets": "سماعات",
};

function StockBadge({ stock, loading }: { stock: number | undefined; loading: boolean }) {
  const tc = useThemeColor();
  if (loading || stock === undefined) return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse"
      style={{ background: hexAlpha(tc.primary, 0.08), color: hexAlpha(tc.primary, 0.4), fontFamily: "'Tajawal', sans-serif" }}>
      <i className="ri-loader-4-line animate-spin text-[10px]"></i>جاري التحقق
    </span>
  );
  if (stock === 0) return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20"
      style={{ fontFamily: "'Tajawal', sans-serif" }}>
      <i className="ri-close-circle-fill text-[10px]"></i>نفد
    </span>
  );
  if (stock <= 3) return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20"
      style={{ fontFamily: "'Tajawal', sans-serif" }}>
      <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-ping inline-block flex-shrink-0"></span>
      آخر {stock}!
    </span>
  );
  if (stock <= 10) return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400/10 text-amber-400 border border-amber-400/20"
      style={{ fontFamily: "'Tajawal', sans-serif" }}>
      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse inline-block flex-shrink-0"></span>
      باقي {stock}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-400/10 text-emerald-400 border border-emerald-400/20"
      style={{ fontFamily: "'Tajawal', sans-serif" }}>
      <i className="ri-checkbox-circle-fill text-[10px]"></i>{stock} قطعة
    </span>
  );
}

/* ══════════════════════════════════════════
   PRODUCT SPECS MODAL
══════════════════════════════════════════ */
function ProductSpecsModal({
  product,
  liveStock,
  stockLoading,
  isInCart,
  onAdd,
  onClose,
}: {
  product: ProductDetail;
  liveStock: number | undefined;
  stockLoading: boolean;
  isInCart: boolean;
  onAdd: () => void;
  onClose: () => void;
}) {
  const tc = useThemeColor();
  const dt = useDesignTokens();
  const [activeTab, setActiveTab] = useState<"highlights" | "specs" | "box">("highlights");
  const [imgIdx, setImgIdx] = useState(0);
  const [added, setAdded] = useState(false);

  const stock = liveStock ?? product.stock ?? 0;
  const isOutOfStock = !stockLoading && stock === 0;
  const disc = product.originalPrice > product.price
    ? Math.round((1 - product.price / product.originalPrice) * 100)
    : 0;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const handleAdd = () => {
    onAdd();
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  // Button style helper
  const getAddBtnStyle = (active: boolean) => {
    if (active) return { background: '#10b981', color: 'white' };
    if (dt.buttonStyle === 'outlined') return {
      background: 'transparent',
      color: tc.primary,
      border: `2px solid ${tc.primary}`,
    };
    if (dt.buttonStyle === 'soft') return {
      background: hexAlpha(tc.primary, 0.15),
      color: tc.primary,
    };
    return {
      background: `linear-gradient(to right, ${tc.primary}, ${tc.primaryDark})`,
      color: 'white',
    };
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        style={{
          background: "#111",
          border: `1px solid ${hexAlpha(tc.primary, 0.2)}`,
          borderRadius: dt.radius,
        }}
        dir="rtl"
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold px-3 py-1"
              style={{
                background: hexAlpha(tc.primary, 0.15),
                color: tc.primary,
                fontFamily: dt.font,
                borderRadius: dt.radiusFull,
              }}>
              {product.category}
            </span>
            {product.badge && (
              <span className="text-xs font-bold px-3 py-1 text-black"
                style={{ background: tc.primary, fontFamily: dt.font, borderRadius: dt.radiusFull }}>
                {product.badge}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center border border-white/10 text-gray-400 hover:text-white hover:border-white/30 transition-all cursor-pointer"
            style={{ borderRadius: dt.radiusSm }}
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* ── Body ── */}
        <div className="overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {/* Left: Image gallery */}
            <div className="bg-[#0d0d0d] p-6 flex flex-col gap-4">
              <div className="relative w-full h-64 md:h-80 overflow-hidden bg-[#0a0a0a]" style={{ borderRadius: dt.radius }}>
                {product.images?.[imgIdx] ? (
                  <img src={product.images[imgIdx]} alt={product.nameAr} className="w-full h-full object-cover object-top" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-image-line text-gray-700 text-5xl"></i>
                  </div>
                )}
                {disc > 0 && (
                  <span className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-2.5 py-1"
                    style={{ fontFamily: dt.font, borderRadius: dt.radiusFull }}>
                    -{disc}%
                  </span>
                )}
                {isOutOfStock && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center" style={{ borderRadius: dt.radius }}>
                    <span className="bg-red-500 text-white text-sm font-bold px-4 py-2"
                      style={{ fontFamily: dt.font, borderRadius: dt.radiusFull }}>
                      نفد المخزون
                    </span>
                  </div>
                )}
              </div>

              {product.images?.length > 1 && (
                <div className="flex gap-2 flex-wrap">
                  {product.images.map((img, i) => (
                    <button key={i} onClick={() => setImgIdx(i)}
                      className="w-14 h-14 overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0"
                      style={{ borderColor: imgIdx === i ? tc.primary : "rgba(255,255,255,0.1)", borderRadius: dt.radiusSm }}>
                      <img src={img} alt="" className="w-full h-full object-cover object-top" />
                    </button>
                  ))}
                </div>
              )}

              <div className="mt-2">
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="text-3xl font-bold"
                    style={{
                      fontFamily: dt.font,
                      background: isOutOfStock ? undefined : `linear-gradient(to left, ${tc.primaryLight}, ${tc.primary})`,
                      WebkitBackgroundClip: isOutOfStock ? undefined : "text",
                      WebkitTextFillColor: isOutOfStock ? undefined : "transparent",
                      backgroundClip: isOutOfStock ? undefined : "text",
                      color: isOutOfStock ? "#6b7280" : undefined,
                    }}>
                    {product.price.toLocaleString()} ر.س
                  </span>
                  {product.originalPrice > product.price && (
                    <span className="text-gray-600 text-base line-through" style={{ fontFamily: dt.font }}>
                      {product.originalPrice.toLocaleString()}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  {isOutOfStock ? (
                    <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 px-4 py-3" style={{ borderRadius: dt.radius }}>
                      <i className="ri-close-circle-fill text-red-400"></i>
                      <span className="text-red-400 text-sm font-bold" style={{ fontFamily: dt.font }}>غير متوفر حالياً</span>
                    </div>
                  ) : (
                    <button
                      onClick={handleAdd}
                      className="flex items-center justify-center gap-2 w-full py-3.5 font-bold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
                      style={{ fontFamily: dt.font, borderRadius: dt.radius, ...getAddBtnStyle(added || isInCart) }}
                    >
                      <i className={`text-base ${added || isInCart ? "ri-check-line" : "ri-shopping-cart-line"}`}></i>
                      {added || isInCart ? "تمت الإضافة للسلة!" : "أضف للسلة"}
                    </button>
                  )}
                  <Link
                    to={`/product/${product.id}`}
                    onClick={onClose}
                    className="flex items-center justify-center gap-2 w-full py-3 font-bold text-sm transition-all cursor-pointer whitespace-nowrap border border-white/10 text-gray-400"
                    style={{ fontFamily: dt.font, borderRadius: dt.radius }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(tc.primary, 0.4);
                      (e.currentTarget as HTMLElement).style.color = tc.primary;
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.1)";
                      (e.currentTarget as HTMLElement).style.color = "#9ca3af";
                    }}
                  >
                    <i className="ri-external-link-line text-sm"></i>
                    صفحة المنتج الكاملة
                  </Link>
                </div>
              </div>
            </div>

            {/* Right: Details */}
            <div className="p-6 flex flex-col gap-5">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2 leading-snug" style={{ fontFamily: dt.font }}>
                  {product.nameAr}
                </h2>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm" style={{ color: i < product.rating ? tc.primary : "#374151" }}></i>
                    ))}
                  </div>
                  <span className="text-gray-500 text-xs" style={{ fontFamily: dt.font }}>({product.reviews} تقييم)</span>
                  <StockBadge stock={liveStock} loading={stockLoading} />
                </div>
                {product.shortDesc && (
                  <p className="text-gray-500 text-sm mt-3 leading-relaxed" style={{ fontFamily: dt.font }}>{product.shortDesc}</p>
                )}
              </div>

              {/* Tabs */}
              <div className="flex gap-1 bg-[#0a0a0a] p-1" style={{ borderRadius: dt.radius }}>
                {[
                  { key: "highlights", label: "المميزات", icon: "ri-star-line" },
                  { key: "specs",      label: "المواصفات", icon: "ri-cpu-line" },
                  { key: "box",        label: "الصندوق",  icon: "ri-box-3-line" },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setActiveTab(t.key as typeof activeTab)}
                    className="flex-1 flex items-center justify-center gap-1.5 text-xs font-bold py-2 transition-all duration-200 cursor-pointer whitespace-nowrap"
                    style={{
                      fontFamily: dt.font,
                      borderRadius: dt.radiusSm,
                      background: activeTab === t.key ? hexAlpha(tc.primary, 0.15) : "transparent",
                      color: activeTab === t.key ? tc.primary : "#6b7280",
                      border: activeTab === t.key ? `1px solid ${hexAlpha(tc.primary, 0.25)}` : "1px solid transparent",
                    }}
                  >
                    <i className={`${t.icon} text-[11px]`}></i>
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto max-h-64 md:max-h-80 pr-1"
                style={{ scrollbarWidth: "thin", scrollbarColor: `${hexAlpha(tc.primary, 0.3)} transparent` }}>
                {activeTab === "highlights" && (
                  <ul className="space-y-2">
                    {(product.highlights ?? []).map((h, i) => (
                      <li key={i} className="flex items-start gap-3 bg-[#0d0d0d] px-4 py-3 border border-white/5" style={{ borderRadius: dt.radius }}>
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-double-line text-sm" style={{ color: tc.primary }}></i>
                        </span>
                        <span className="text-gray-300 text-sm leading-relaxed" style={{ fontFamily: dt.font }}>{h}</span>
                      </li>
                    ))}
                    {product.longDesc && (
                      <li className="bg-[#0d0d0d] px-4 py-3 border border-white/5 mt-2" style={{ borderRadius: dt.radius }}>
                        <p className="text-gray-400 text-xs leading-loose" style={{ fontFamily: dt.font }}>{product.longDesc}</p>
                      </li>
                    )}
                  </ul>
                )}
                {activeTab === "specs" && (
                  <div className="flex flex-col gap-1">
                    {(product.specs ?? []).map((spec, i) => (
                      <div key={i} className={`flex items-start gap-4 px-4 py-3 ${i % 2 === 0 ? "bg-[#0d0d0d]" : "bg-transparent"}`} style={{ borderRadius: dt.radiusSm }}>
                        <span className="text-gray-500 text-xs w-32 flex-shrink-0 pt-0.5" style={{ fontFamily: dt.font }}>{spec.label}</span>
                        <span className="text-gray-200 text-xs font-medium flex-1" style={{ fontFamily: dt.font }}>{spec.value}</span>
                      </div>
                    ))}
                  </div>
                )}
                {activeTab === "box" && (
                  <div className="grid grid-cols-1 gap-2">
                    {(product.inBox ?? []).map((item, i) => (
                      <div key={i} className="flex items-center gap-3 bg-[#0d0d0d] border border-white/5 px-4 py-3" style={{ borderRadius: dt.radius }}>
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-archive-line text-sm" style={{ color: tc.primary }}></i>
                        </div>
                        <span className="text-gray-300 text-sm" style={{ fontFamily: dt.font }}>{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   PRODUCT CARD
══════════════════════════════════════════ */
function ProductCard({
  product, liveStock, stockLoading, isInCart, justAdded, onAdd, onShowSpecs,
}: {
  product: ProductDetail;
  liveStock: number | undefined;
  stockLoading: boolean;
  isInCart: boolean;
  justAdded: boolean;
  onAdd: () => void;
  onShowSpecs: () => void;
}) {
  const tc = useThemeColor();
  const dt = useDesignTokens();
  const [tab, setTab] = useState<"highlights" | "specs">("highlights");

  const stock = liveStock ?? product.stock ?? 0;
  const isOutOfStock = !stockLoading && stock === 0;
  const disc = product.originalPrice > product.price
    ? Math.round((1 - product.price / product.originalPrice) * 100)
    : 0;

  const topHighlights = (product.highlights ?? []).slice(0, 4);
  const topSpecs = (product.specs ?? []).slice(0, 4);

  // Card border style based on cardStyle token
  const getCardBorder = () => {
    if (dt.cardStyle === 'flat') return 'none';
    if (dt.cardStyle === 'shadow') return '1px solid rgba(255,255,255,0.03)';
    if (dt.cardStyle === 'glass') return `1px solid ${hexAlpha(tc.primary, 0.12)}`;
    return '1px solid rgba(255,255,255,0.05)'; // border (default)
  };

  const getCardBg = () => {
    if (dt.cardStyle === 'glass') return `rgba(255,255,255,0.04)`;
    return '#111';
  };

  // Add-to-cart button style
  const getCartBtnStyle = (active: boolean) => {
    if (active) return { background: '#10b981', color: 'white', boxShadow: '0 0 15px rgba(52,211,153,0.3)' };
    if (dt.buttonStyle === 'outlined') return {
      background: 'transparent',
      color: tc.primary,
      border: `2px solid ${tc.primary}`,
      boxShadow: 'none',
    };
    if (dt.buttonStyle === 'soft') return {
      background: hexAlpha(tc.primary, 0.15),
      color: tc.primary,
      boxShadow: 'none',
    };
    return {
      background: `linear-gradient(135deg, ${tc.primary}, ${tc.primaryDark})`,
      color: 'white',
      boxShadow: `0 0 15px ${hexAlpha(tc.primary, 0.2)}`,
    };
  };

  return (
    <div
      className="group relative overflow-hidden transition-all duration-300 flex flex-col"
      style={{
        background: getCardBg(),
        border: getCardBorder(),
        borderRadius: dt.radius,
        backdropFilter: dt.cardStyle === 'glass' ? 'blur(12px)' : undefined,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(tc.primary, 0.22);
        if (dt.cardStyle !== 'flat') {
          (e.currentTarget as HTMLElement).style.boxShadow = dt.cardStyle === 'shadow'
            ? `0 8px 40px ${hexAlpha(tc.primary, 0.12)}`
            : `0 0 40px ${hexAlpha(tc.primary, 0.07)}`;
        }
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = getCardBorder().includes('rgba') ? '' : 'rgba(255,255,255,0.05)';
        (e.currentTarget as HTMLElement).style.boxShadow = 'none';
      }}
    >
      {/* Image */}
      <Link to={`/product/${product.id}`}
        className="block relative w-full h-56 overflow-hidden bg-[#0f0f0f] cursor-pointer flex-shrink-0"
        style={{ borderRadius: `${dt.radius} ${dt.radius} 0 0` }}>
        {product.images?.[0] ? (
          <img src={product.images[0]} alt={product.nameAr}
            className={`w-full h-full object-cover object-top transition-transform duration-500 ${isOutOfStock ? "grayscale-[40%]" : "group-hover:scale-105"}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <i className="ri-image-line text-gray-700 text-4xl"></i>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        {isOutOfStock && (
          <div className="absolute inset-0 bg-black/55 flex items-end justify-center pb-4">
            <span className="bg-red-500 text-white text-xs font-bold px-4 py-1.5 whitespace-nowrap"
              style={{ fontFamily: dt.font, borderRadius: dt.radiusFull }}>
              <i className="ri-close-circle-line ml-1 text-xs"></i>نفد المخزون
            </span>
          </div>
        )}
        <div className="absolute top-3 right-3 flex flex-col gap-1.5">
          {product.badge && (
            <span className="text-xs font-black px-3 py-1 whitespace-nowrap"
              style={{ background: `linear-gradient(to right, ${tc.primary}, ${tc.primaryDark})`, color: tc.textOnPrimary, fontFamily: dt.font, borderRadius: dt.radiusFull }}>
              {product.badge}
            </span>
          )}
          {disc > 0 && (
            <span className="bg-red-500/90 text-white text-xs font-black px-2.5 py-1 whitespace-nowrap"
              style={{ fontFamily: dt.font, borderRadius: dt.radiusFull }}>
              -{disc}%
            </span>
          )}
        </div>
        {!stockLoading && (() => {
          const pct = Math.min(100, Math.round((stock / Math.max(stock, 20)) * 100));
          const color = stock === 0 ? "#ef4444" : stock <= 3 ? "#ef4444" : stock <= 10 ? "#f59e0b" : "#10b981";
          return (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/40">
              <div className="h-full transition-all duration-700" style={{ width: `${pct}%`, background: color }}></div>
            </div>
          );
        })()}
      </Link>

      {/* Info */}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-xs tracking-wide" style={{ fontFamily: dt.font, color: hexAlpha(tc.primary, 0.5) }}>
            {product.category}
          </p>
          <StockBadge stock={liveStock} loading={stockLoading} />
        </div>

        <Link to={`/product/${product.id}`}
          className="block font-bold text-base mb-1.5 leading-snug transition-colors duration-200 cursor-pointer text-white"
          style={{ fontFamily: dt.font }}
          onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = tc.primary}
          onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'white'}>
          {product.nameAr}
        </Link>

        {product.shortDesc && (
          <p className="text-gray-500 text-xs mb-3 leading-relaxed line-clamp-2" style={{ fontFamily: dt.font }}>
            {product.shortDesc}
          </p>
        )}

        <div className="flex items-center gap-1 mb-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <i key={i} className="ri-star-fill text-xs" style={{ color: i < product.rating ? tc.primary : '#374151' }}></i>
          ))}
          <span className="text-gray-500 text-xs mr-1.5" style={{ fontFamily: dt.font }}>({product.reviews})</span>
        </div>

        {/* Mini tabs */}
        {(topHighlights.length > 0 || topSpecs.length > 0) && (
          <div className="mb-4 flex-1">
            <div className="flex gap-1 mb-3 bg-[#0a0a0a] p-1" style={{ borderRadius: dt.radiusSm }}>
              <button
                onClick={() => setTab("highlights")}
                className="flex-1 text-[11px] font-bold py-1.5 transition-all duration-200 cursor-pointer whitespace-nowrap"
                style={{
                  fontFamily: dt.font,
                  borderRadius: `calc(${dt.radiusSm} - 2px)`,
                  background: tab === "highlights" ? hexAlpha(tc.primary, 0.15) : "transparent",
                  color: tab === "highlights" ? tc.primary : "#6b7280",
                  border: tab === "highlights" ? `1px solid ${hexAlpha(tc.primary, 0.2)}` : "1px solid transparent",
                }}>
                <i className="ri-star-line ml-1 text-[10px]"></i>المميزات
              </button>
              <button
                onClick={() => setTab("specs")}
                className="flex-1 text-[11px] font-bold py-1.5 transition-all duration-200 cursor-pointer whitespace-nowrap"
                style={{
                  fontFamily: dt.font,
                  borderRadius: `calc(${dt.radiusSm} - 2px)`,
                  background: tab === "specs" ? hexAlpha(tc.primary, 0.15) : "transparent",
                  color: tab === "specs" ? tc.primary : "#6b7280",
                  border: tab === "specs" ? `1px solid ${hexAlpha(tc.primary, 0.2)}` : "1px solid transparent",
                }}>
                <i className="ri-cpu-line ml-1 text-[10px]"></i>المواصفات
              </button>
            </div>

            {tab === "highlights" && topHighlights.length > 0 && (
              <ul className="space-y-1.5">
                {topHighlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-gray-400" style={{ fontFamily: dt.font }}>
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-[11px]" style={{ color: tc.primary }}></i>
                    </span>
                    <span className="leading-relaxed">{h}</span>
                  </li>
                ))}
              </ul>
            )}

            {tab === "specs" && topSpecs.length > 0 && (
              <div className="space-y-1.5">
                {topSpecs.map((s, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-xs">
                    <span className="text-gray-600 whitespace-nowrap flex-shrink-0" style={{ fontFamily: dt.font }}>{s.label}</span>
                    <span className="text-gray-300 text-left truncate" style={{ fontFamily: dt.font }}>{s.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Price + cart */}
        <div className="flex items-center justify-between mb-3 mt-auto">
          <div>
            <span className="font-bold text-xl"
              style={{
                fontFamily: dt.font,
                background: isOutOfStock ? undefined : `linear-gradient(to left, ${tc.primaryLight}, ${tc.primary})`,
                WebkitBackgroundClip: isOutOfStock ? undefined : "text",
                WebkitTextFillColor: isOutOfStock ? undefined : "transparent",
                backgroundClip: isOutOfStock ? undefined : "text",
                color: isOutOfStock ? "#6b7280" : undefined,
              }}>
              {product.price.toLocaleString()} ر.س
            </span>
            {product.originalPrice > product.price && (
              <span className="text-gray-600 text-xs line-through mr-2" style={{ fontFamily: dt.font }}>
                {product.originalPrice.toLocaleString()}
              </span>
            )}
          </div>
          {isOutOfStock ? (
            <div className="w-10 h-10 flex items-center justify-center bg-white/5 cursor-not-allowed" style={{ borderRadius: dt.radiusSm }}>
              <i className="ri-close-line text-gray-600 text-sm"></i>
            </div>
          ) : (
            <button
              onClick={onAdd}
              className="w-10 h-10 flex items-center justify-center transition-all duration-300 cursor-pointer"
              style={{ borderRadius: dt.radiusSm, ...getCartBtnStyle(justAdded || isInCart) }}>
              <i className={`text-sm ${justAdded || isInCart ? "ri-check-line" : "ri-shopping-cart-line"}`}></i>
            </button>
          )}
        </div>

        {/* Specs button */}
        <button
          onClick={onShowSpecs}
          className="flex items-center justify-center gap-2 w-full border border-white/8 text-gray-500 text-xs py-2.5 transition-all duration-300 cursor-pointer"
          style={{ fontFamily: dt.font, borderRadius: dt.radius }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(tc.primary, 0.4);
            (e.currentTarget as HTMLElement).style.color = tc.primary;
            (e.currentTarget as HTMLElement).style.background = hexAlpha(tc.primary, 0.05);
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)';
            (e.currentTarget as HTMLElement).style.color = '#6b7280';
            (e.currentTarget as HTMLElement).style.background = 'transparent';
          }}
        >
          <i className="ri-list-check-3 text-sm"></i>
          <span>عرض المواصفات الكاملة</span>
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   MAIN SECTION
══════════════════════════════════════════ */
export default function ProductsSection() {
  const [active, setActive] = useState("الكل");
  const [addedId, setAddedId] = useState<number | null>(null);
  const [specsProduct, setSpecsProduct] = useState<ProductDetail | null>(null);
  const { addItem, items } = useCart();
  const { products } = useProducts();
  const tc = useThemeColor();
  const dt = useDesignTokens();
  const { bg: sectionBg } = useSectionBg('sectionProducts');
  const productIds = useMemo(() => products.map((p) => p.id), [products]);
  const { getStock, loading: stockLoading } = useProductsStock(productIds);

  const filtered = active === "الكل" ? products : products.filter((p) => p.category === active);
  const isInCart = (id: number) => items.some((i) => i.id === id);

  const handleAdd = (product: ProductDetail) => {
    addItem({
      id: product.id,
      nameAr: product.nameAr,
      price: product.price,
      image: product.images?.[0] ?? '',
      category: product.category,
    });
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 2000);
  };

  return (
    <section id="products" className="py-24 px-4" style={{ background: sectionBg }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-xs tracking-[0.4em] uppercase mb-4"
            style={{ fontFamily: dt.font, color: hexAlpha(tc.primary, 0.7) }}>
            تشكيلتنا المميزة
          </p>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-5"
            style={{ fontFamily: dt.font, filter: `drop-shadow(0 0 30px ${hexAlpha(tc.primary, 0.15)})` }}>
            منتجاتنا
          </h2>
          <div className="w-20 h-0.5 mx-auto"
            style={{ background: `linear-gradient(to right, transparent, ${tc.primary}, transparent)` }}></div>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActive(cat)}
              className="px-5 py-2.5 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{
                fontFamily: dt.font,
                borderRadius: dt.radiusFull,
                background: active === cat ? `linear-gradient(to right, ${tc.primary}, ${tc.primaryDark})` : '#141414',
                color: active === cat ? tc.textOnPrimary : '#9ca3af',
                border: active === cat ? 'none' : '1px solid rgba(255,255,255,0.08)',
                boxShadow: active === cat ? `0 0 20px ${hexAlpha(tc.primary, 0.2)}` : 'none',
              }}
              onMouseEnter={e => { if (active !== cat) (e.currentTarget as HTMLElement).style.color = 'white'; }}
              onMouseLeave={e => { if (active !== cat) (e.currentTarget as HTMLElement).style.color = '#9ca3af'; }}
            >
              {categoryLabels[cat] ?? cat}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center bg-[#111] border border-white/5 mx-auto mb-4" style={{ borderRadius: dt.radius }}>
              <i className="ri-inbox-line text-3xl text-gray-700"></i>
            </div>
            <p className="text-gray-500 text-sm" style={{ fontFamily: dt.font }}>لا توجد منتجات في هذه الفئة</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
            {filtered.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                liveStock={stockLoading ? undefined : getStock(product.id)}
                stockLoading={stockLoading}
                isInCart={isInCart(product.id)}
                justAdded={addedId === product.id}
                onAdd={() => handleAdd(product)}
                onShowSpecs={() => setSpecsProduct(product)}
              />
            ))}
          </div>
        )}
      </div>

      {specsProduct && (
        <ProductSpecsModal
          product={specsProduct}
          liveStock={stockLoading ? undefined : getStock(specsProduct.id)}
          stockLoading={stockLoading}
          isInCart={isInCart(specsProduct.id)}
          onAdd={() => handleAdd(specsProduct)}
          onClose={() => setSpecsProduct(null)}
        />
      )}
    </section>
  );
}
